import { useMemo, createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { Configuration } from "@freightsimple/generated-hubble-openapi-client/runtime";
import {
  forceFlush,
  mark,
  startTimedSpan,
} from "@freightsimple/observability/observability";
import { AuthRedirectInProgressError } from "@freightsimple/api-runtime/authRedirectError";
import { notifyRefetch } from "@freightsimple/api-runtime/refetchRegistry";
import { getLoginRequest, type HubbleEnvironment } from "./authConfig";
import { READ_METHODS_BY_API } from "./generatedReadMethods";

// Module-level lock: once a redirect is initiated, all subsequent API calls
// short-circuit immediately. Prevents the flood of concurrent acquireTokenRedirect
// calls that cause the redirect loop when tokens are stale.
let authRedirectInitiated = false;

// Session-level counters for the cold-load auth instrumentation. These are
// observed via OTEL spans (auth.silent_token attempt_index / concurrent_inflight)
// and read by the cold-start overlay so we can see fan-out during a cold load.
let silentAttemptIndex = 0;
let silentInflight = 0;

// MSAL's AuthError-derived classes carry errorCode/subError. Extract them as
// flat attributes so we can break down acquireTokenSilent failures in Axiom by
// exact reason (interaction_required, login_required, network_error, etc.)
// rather than only by JS error name.
function msalErrorAttrs(
  error: unknown,
): Record<string, string | number | boolean> {
  const attrs: Record<string, string | number | boolean> = {};
  if (!error || typeof error !== "object") return attrs;
  const err = error as {
    name?: unknown;
    errorCode?: unknown;
    subError?: unknown;
    message?: unknown;
  };
  if (typeof err.name === "string") attrs["error.name"] = err.name;
  if (typeof err.message === "string") attrs["error.message"] = err.message;
  if (typeof err.errorCode === "string")
    attrs["msal.error_code"] = err.errorCode;
  if (typeof err.subError === "string") attrs["msal.sub_error"] = err.subError;
  return attrs;
}

function generateTraceParent(): string {
  const hex = (n: number) =>
    Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
  return `00-${hex(16)}-${hex(8)}-01`;
}

interface ApiConfigurationOptions {
  environment: HubbleEnvironment;
  basePath: string;
  authDisabled?: boolean;
}

const ApiConfigContext = createContext<ApiConfigurationOptions | undefined>(
  undefined,
);

interface ApiConfigProviderProps {
  options: ApiConfigurationOptions;
  children: ReactNode;
}

export function ApiConfigProvider({
  options,
  children,
}: ApiConfigProviderProps) {
  return (
    <ApiConfigContext.Provider value={options}>
      {children}
    </ApiConfigContext.Provider>
  );
}

// Global singleton proxy per API class. Keyed by class constructor, never
// invalidated. Configuration can change identity when MSAL's useAccount()
// returns a new object reference, but it's logically the same config —
// same basePath, same auth. The middleware inside Configuration handles
// token refresh on every request, so a "stale" Configuration still works.
//
// This is critical for auto-refetch: all components must share the exact
// same proxy object so that mutations trigger refetch on the correct listeners.
const instanceCache = new Map<unknown, unknown>();

/**
 * Returns a cached singleton of the given API class for the current Configuration.
 * Same useShipmentsApi() in two components → same instance.
 *
 * The instance is wrapped in a Proxy that auto-notifies the refetch registry
 * after any method that returns a Promise resolves (i.e., any API call).
 * GET calls don't trigger refetch — only mutations (POST/PUT/DELETE).
 * Read methods are identified using a generated set from the OpenAPI spec.
 */
export function useApiInstance<T extends object>(
  ApiClass: new (config: Configuration) => T,
): T {
  const config = useApiConfiguration();
  return useMemo(() => {
    let proxy = instanceCache.get(ApiClass) as T | undefined;
    if (!proxy) {
      const instance = new ApiClass(config);
      // Look up read methods by the class CONSTRUCTOR, not its name. Production
      // minification mangles `ApiClass.name` (e.g. to "e"), so a name-keyed
      // lookup silently misses, every method gets treated as a mutation, and a
      // read called outside useFetch (e.g. a thumbnail loader) triggers an
      // endless notifyRefetch → refetch → re-render → refetch loop. The
      // constructor reference is identical pre/post minification.
      const readMethods = READ_METHODS_BY_API.get(ApiClass);
      proxy = new Proxy(instance, {
        get(target, prop) {
          const value = (target as any)[prop];
          if (typeof value !== "function" || typeof prop !== "string") {
            return value;
          }
          // Read methods — pass through without triggering refetch.
          // readMethods is generated from the OpenAPI spec, scoped to this
          // API class, so classification is exact.
          if (readMethods?.has(prop) || prop.endsWith("Raw")) {
            return value.bind(target);
          }
          // Everything else is treated as a potential mutation.
          // Call the original, then notify refetch on success.
          //
          // Note: Some methods are POST-based reads (e.g., generateProfitLossReport).
          // These will trigger notifyRefetch, but useFetch wraps its calls in
          // withSuppressedRefetch() which prevents the notification from causing
          // an infinite loop. Only direct mutations (e.g., button click handlers
          // calling api.cancelShipment()) will actually trigger refetch of
          // other useFetch hooks watching the same API.
          return async (...args: unknown[]) => {
            const result = await value.apply(target, args);
            notifyRefetch(proxy!);
            return result;
          };
        },
      });
      instanceCache.set(ApiClass, proxy);
    }
    return proxy;
  }, [config, ApiClass]);
}

function useApiConfiguration(): Configuration {
  const options = useContext(ApiConfigContext);
  if (!options) {
    throw new Error(
      "useApiConfiguration must be used within an ApiConfigProvider",
    );
  }

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const loginRequest = getLoginRequest(options.environment);

  return useMemo(
    () =>
      new Configuration({
        basePath: options.basePath,
        middleware: [
          {
            pre: async (context: any) => {
              context.init.headers["traceparent"] = generateTraceParent();

              // In signed-cookie mode (auth disabled / preview) we attach no auth header: the
              // browser sends the same-origin `amy-preview-access` cookie on the proxied /apollo
              // request, and the backend HMAC-verifies it. Production keeps the MSAL bearer path.
              if (!options.authDisabled && account) {
                if (authRedirectInitiated) {
                  mark("auth.redirect_in_progress_thrown", {
                    where: "entry",
                  });
                  throw new AuthRedirectInProgressError();
                }
                const attemptIndex = silentAttemptIndex++;
                silentInflight++;
                const span = startTimedSpan("auth.silent_token", {
                  attempt_index: attemptIndex,
                  concurrent_inflight: silentInflight,
                  redirect_initiated_before: authRedirectInitiated,
                });
                try {
                  const response = await instance.acquireTokenSilent({
                    scopes: loginRequest.scopes,
                    account,
                  });
                  context.init.headers["Authorization"] =
                    `Bearer ${response.accessToken}`;
                  span.end({ result: "success" });
                } catch (e) {
                  // acquireTokenSilent can fail due to timeout, expired refresh
                  // token, or network issues. Fall back to a full redirect login
                  // to get a fresh session.
                  // Re-check the lock: another concurrent request may have
                  // already initiated the redirect while we were awaiting.
                  if (authRedirectInitiated) {
                    span.recordError(e);
                    span.end({
                      result: "short_circuit",
                      ...msalErrorAttrs(e),
                    });
                    mark("auth.redirect_in_progress_thrown", {
                      where: "after_failure",
                    });
                    throw new AuthRedirectInProgressError();
                  }
                  authRedirectInitiated = true;
                  span.recordError(e);
                  // End the span BEFORE forceFlush(): BatchSpanProcessor only
                  // exports already-ended spans, so flushing while the span
                  // is still open would drop the very span we want to ship
                  // before acquireTokenRedirect navigates the page away.
                  span.end({ result: "redirecting", ...msalErrorAttrs(e) });
                  console.warn(
                    "acquireTokenSilent failed, redirecting to login",
                    e,
                  );
                  mark("auth.redirect_initiated", {
                    attempt_index: attemptIndex,
                  });
                  await forceFlush();
                  try {
                    await instance.acquireTokenRedirect({
                      scopes: loginRequest.scopes,
                      account,
                    });
                  } catch (redirectError) {
                    // Redirect itself failed (e.g. interaction_in_progress,
                    // network error). Release the lock so subsequent calls
                    // can retry instead of leaving the user on a dead page.
                    // Span is already ended; emit a separate mark to capture
                    // the failure reason.
                    authRedirectInitiated = false;
                    mark("auth.redirect_failed", msalErrorAttrs(redirectError));
                    throw redirectError;
                  }
                  throw new AuthRedirectInProgressError();
                } finally {
                  silentInflight--;
                }
              }

              return context;
            },
          },
        ],
      }),
    [instance, account, options.basePath, options.authDisabled],
  );
}
